/**
 * OWL-ASSISTANT-BUTTON
 * L'assistant sur un champ : il propose quand c'est vide, retravaille quand c'est rempli,
 * et sait revenir en arriere. Le texte s'ecrit au fil de l'eau et le bouton reste toujours
 * actif : une proposition se relance autant de fois qu'on veut.
 */
import { useEffect, useRef, useState } from "react"
import {
  faLightbulb,
  faPenFancy,
  faRotateLeft,
  faSpellCheck,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"

import ActionsMenu from "./actions-menu"
import Tooltip from "./tooltip"
import {
  ASSISTANT_INSTANT,
  askAssistantText,
  AssistantCancel,
  getFixPrompt,
  getRephrasePrompt
} from "@/utils/assistant.utils"

interface Props {
  //La valeur du champ pilote : vide, le hibou propose ; remplie, il retravaille
  value: string
  //Proposer a partir du contexte, sans tenir compte de ce qui est ecrit
  ideaPrompt?: string
  //Reprendre ce qui est ecrit pour en faire une meilleure version
  improvePrompt?: string
  //Ouvre le menu au-dessus du bouton pour les champs situes en bas d'un conteneur
  isContextMenuTop?: boolean
  onChange: (value: string) => void
  //Le champ se verrouille le temps que l'assistant ecrive dedans
  onRunningChange?: (isRunning: boolean) => void
}

export function OwlAssistantButton(props: Props) {
  const { ideaPrompt, improvePrompt } = props
  const value = props.value ?? ""

  const [isRunning, setIsRunning] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  //Valeur d'avant la derniere proposition : elle seule permet d'annuler
  const [snapshot, setSnapshot] = useState<string | null>(null)

  const cancelRun = useRef<AssistantCancel | null>(null)
  //Dernier texte ecrit par l'assistant : il dit si la valeur bouge de notre fait ou du sien
  const lastWritten = useRef<string | null>(null)

  //Une saisie manuelle devient la nouvelle reference : il n'y a plus de proposition a annuler
  useEffect(() => {
    if (isRunning) return
    if (lastWritten.current === null) return
    if (value === lastWritten.current) return

    lastWritten.current = null
    setSnapshot(null)
  }, [value, isRunning])

  //Quitter l'ecran en pleine reponse ne doit pas laisser la conversation ouverte
  useEffect(() => {
    return () => {
      if (cancelRun.current) cancelRun.current()
    }
  }, [])

  function setRunning(isRunningNow: boolean) {
    setIsRunning(isRunningNow)
    if (props.onRunningChange) props.onRunningChange(isRunningNow)
  }

  function write(text: string) {
    lastWritten.current = text
    props.onChange(text)
  }

  //Remet la valeur d'avant : il n'y a alors plus rien a annuler
  function restore(text: string) {
    props.onChange(text)
    lastWritten.current = null
    setSnapshot(null)
  }

  function run(prompt: string, profile?: { model: string; reasoningEffort?: string }) {
    //La valeur d'avant se capture ici : les reponses arrivent trop tard pour la relire
    const previous = value

    setIsMenuOpen(false)
    setSnapshot(previous)
    setRunning(true)

    cancelRun.current = askAssistantText({
      prompt,
      profile,
      //Le flux ouvre sur un morceau vide : le texte d'avant reste tant que rien n'est arrive
      onToken: (text) => {
        if (text) write(text)
      },
      onDone: (text) => {
        cancelRun.current = null
        setRunning(false)
        //Rien recu : le texte d'avant vaut mieux qu'un champ vide
        if (text) write(text)
        else restore(previous)
      },
      onError: () => {
        cancelRun.current = null
        setRunning(false)
        restore(previous)
        toast("L'assistant n'a pas répondu.", { type: "error" })
      }
    })
  }

  //Arreter garde ce qui est deja ecrit : c'est une reponse courte, pas une erreur
  function stop() {
    if (cancelRun.current) cancelRun.current()
    cancelRun.current = null
    setRunning(false)
  }

  function handleClick() {
    if (isRunning) return stop()

    //Champ vide : il n'y a qu'une chose a faire, autant la faire tout de suite
    if (!value.trim() && ideaPrompt) return run(ideaPrompt)

    setIsMenuOpen(!isMenuOpen)
  }

  function getTooltip() {
    if (isRunning) return "Arrêter"
    if (!value.trim()) return "Trouver une idée"
    return "Utiliser l'assistant"
  }

  const menuActions = [
    ...(improvePrompt ? [{ id: "IMPROVE", icon: faWandMagicSparkles, label: "Compléter" }] : []),
    //Les deux passes sur la langue vont ensemble et restent au meme endroit : un menu qui bouge
    //deroute plus que le contresens que Reformuler peut sortir d'un texte court (cf. assistant.utils)
    ...(value.trim()
      ? [
          { id: "REPHRASE", icon: faPenFancy, label: "Reformuler" },
          { id: "FIX", icon: faSpellCheck, label: "Corriger les fautes" }
        ]
      : []),
    ...(ideaPrompt ? [{ id: "IDEA", icon: faLightbulb, label: "Autre idée" }] : []),
    ...(snapshot !== null ? [{ id: "RESTORE", icon: faRotateLeft, label: "Annuler la proposition" }] : [])
  ]

  function handleMenuSelect(actionId: string) {
    if (actionId === "IMPROVE" && improvePrompt) {
      run(improvePrompt)
      return
    }

    if (actionId === "REPHRASE") {
      run(getRephrasePrompt(value), ASSISTANT_INSTANT)
      return
    }

    if (actionId === "FIX") {
      run(getFixPrompt(value), ASSISTANT_INSTANT)
      return
    }

    if (actionId === "IDEA" && ideaPrompt) {
      run(ideaPrompt)
      return
    }

    if (actionId === "RESTORE" && snapshot !== null) {
      restore(snapshot)
    }
  }

  if (!ideaPrompt && !improvePrompt) return null

  return (
    <div className="rel">
      <Tooltip text={getTooltip()} position="left">
        <button
          className={`owl-assistant-button
            ${isRunning ? "owl-assistant-button-running" : ""}
            ${!value.trim() && !isRunning ? "owl-assistant-button-idle" : ""}
            ${value.trim() && !isRunning ? "owl-assistant-button-wand" : ""}
          `}
          onClick={handleClick}
        />
      </Tooltip>

      {isMenuOpen && (
        <ActionsMenu
          actions={menuActions}
          positionBottom={props.isContextMenuTop}
          positionRight
          onClose={() => setIsMenuOpen(false)}
          onSelect={handleMenuSelect}
        />
      )}
    </div>
  )
}
